import { useState, type FormEvent } from 'react'
import { links, type LinkKind, type ShareLink } from '../api'
import { formatDate } from '../format'

const EXPIRY_CHOICES: { label: string; hours: number | null }[] = [
  { label: '1시간', hours: 1 },
  { label: '1일', hours: 24 },
  { label: '7일', hours: 24 * 7 },
  { label: '30일', hours: 24 * 30 },
  { label: '무기한', hours: null },
]

/**
 * Creates a link that hands one file to someone with no account, or takes files
 * from them into one folder.
 *
 * Defaults to seven days: an expiry the sender picked is far more likely to be
 * right than one that never comes, and an unlimited link is the one that leaks
 * a year later. An upload link cannot be unlimited at all — see routes/links.ts.
 */
export default function ShareLinkDialog({
  rootId,
  path,
  filename,
  kind,
  onClose,
}: {
  rootId: string
  path: string
  filename: string
  kind: LinkKind
  onClose: () => void
}) {
  const receiving = kind === 'upload'
  const [hours, setHours] = useState<number | null>(24 * 7)
  const [usePassword, setUsePassword] = useState(false)
  const [password, setPassword] = useState('')
  const [useLimit, setUseLimit] = useState(receiving)
  const [limit, setLimit] = useState('1')
  const [created, setCreated] = useState<ShareLink | null>(null)
  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      setCreated(
        await links.create({
          rootId,
          path,
          kind,
          expiresInHours: hours,
          password: usePassword && password ? password : null,
          maxUses: useLimit ? Number(limit) : null,
        }),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function copy() {
    if (!created) return
    await navigator.clipboard.writeText(created.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="share-title">
        <h2 id="share-title">{receiving ? '업로드 링크 만들기' : '공유 링크 만들기'}</h2>
        <p className="mono conflict-name">{filename}</p>

        {created ? (
          <>
            <p className="muted small">
              {receiving
                ? '계정 없이 이 주소로 이 폴더에 파일을 넣을 수 있습니다. 폴더 안은 보이지 않습니다.'
                : '계정 없이 이 주소로 바로 받을 수 있습니다. 카카오톡이나 메일로 보내세요.'}
            </p>
            <p className="mono conflict-name">{created.url}</p>

            <dl className="facts">
              <div>
                <dt>만료</dt>
                <dd>{created.expiresAt ? formatDate(created.expiresAt) : '없음'}</dd>
              </div>
              <div>
                <dt>비밀번호</dt>
                <dd>{created.hasPassword ? '있음' : '없음'}</dd>
              </div>
              <div>
                <dt>{receiving ? '받을 개수' : '다운로드 제한'}</dt>
                <dd>{created.maxUses === null ? '무제한' : `${created.maxUses}${receiving ? '개' : '회'}`}</dd>
              </div>
            </dl>

            <div className="modal-actions">
              <button type="button" className="primary" onClick={() => void copy()}>
                {copied ? '복사됨' : '주소 복사'}
              </button>
              <button type="button" onClick={onClose}>
                닫기
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <label>
              유효 기간
              <select
                value={hours === null ? 'never' : String(hours)}
                onChange={(event) =>
                  setHours(event.target.value === 'never' ? null : Number(event.target.value))
                }
              >
                {EXPIRY_CHOICES.map((choice) => (
                  <option key={choice.label} value={choice.hours === null ? 'never' : choice.hours}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="inline">
              <input
                type="checkbox"
                checked={usePassword}
                onChange={(event) => setUsePassword(event.target.checked)}
              />
              비밀번호 걸기
            </label>
            {usePassword && (
              <input
                type="text"
                placeholder="4자 이상 · 받는 사람에게 따로 알려주세요"
                minLength={4}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            )}

            {receiving ? (
              <label>
                받을 파일 개수
                <input
                  type="number"
                  min={1}
                  value={limit}
                  onChange={(event) => setLimit(event.target.value)}
                  required
                />
              </label>
            ) : (
              <>
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={useLimit}
                    onChange={(event) => setUseLimit(event.target.checked)}
                  />
                  다운로드 횟수 제한
                </label>
                {useLimit && (
                  <input
                    type="number"
                    min={1}
                    value={limit}
                    onChange={(event) => setLimit(event.target.value)}
                    required
                  />
                )}
              </>
            )}

            {error && <p className="error">{error}</p>}

            <div className="modal-actions">
              <button type="submit" className="primary" disabled={busy}>
                {busy ? '만드는 중…' : '링크 만들기'}
              </button>
              <button type="button" onClick={onClose}>
                취소
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
